/**
 * Gas Volume Calculator
 *
 * Takes room dimensions (length x width x height, in feet), calculates the
 * cubic footage, and recommends both Fast and Slow Release ClO2 options.
 *
 * Tiers are supplied by the section via the `data-tiers` attribute as JSON:
 *   [{ "name": "...", "max": 1000, "url": "...", "price": "$15" }, ...]
 * sorted ascending by `max`. The smallest tier whose `max` covers the
 * calculated volume is recommended. Fast Release is always the primary
 * recommendation, per the product protocol.
 */
class GasCalculator extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-gas-form]');
    this.result = this.querySelector('[data-gas-result]');
    this.volumeEl = this.querySelector('[data-gas-volume]');
    this.recEl = this.querySelector('[data-gas-rec]');
    this.errorEl = this.querySelector('[data-gas-error]');

    this.fastTiers = this.#parseTiers(this.dataset.fastTiers || this.dataset.tiers);
    this.slowTiers = this.#parseTiers(this.dataset.slowTiers);
    this.tabletVolume = Number(this.dataset.tabletVolume) || 1000;
    this.fallbackUrl = this.dataset.fallbackUrl || '';

    if (this.form) {
      this.form.addEventListener('submit', this.#handleSubmit);
      this.form.addEventListener('reset', this.#handleReset);
    }
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.#handleSubmit);
    this.form?.removeEventListener('reset', this.#handleReset);
  }

  #parseTiers(source) {
    try {
      const tiers = JSON.parse(source || '[]');
      return tiers
        .filter((t) => Number(t.max) > 0)
        .sort((a, b) => Number(a.max) - Number(b.max));
    } catch {
      return [];
    }
  }

  #handleSubmit = (event) => {
    event.preventDefault();

    const length = this.#value('length');
    const width = this.#value('width');
    const height = this.#value('height');

    if (![length, width, height].every((n) => Number.isFinite(n) && n > 0)) {
      this.#showError('Enter a length, width, and height greater than zero.');
      return;
    }

    this.#clearError();
    const volume = Math.round(length * width * height);
    this.#render(volume);
  };

  #handleReset = () => {
    this.#clearError();
    if (this.result) this.result.hidden = true;
  };

  #value(name) {
    const input = this.form?.elements.namedItem(name);
    return input ? parseFloat(input.value) : NaN;
  }

  #render(volume) {
    if (this.volumeEl) this.volumeEl.textContent = volume.toLocaleString();

    const fastTier = this.fastTiers.find((t) => volume <= Number(t.max));
    const slowTier = this.slowTiers.find((t) => volume <= Number(t.max));
    const tablets = Math.ceil(volume / this.tabletVolume);
    const tabletLabel = tablets === 1 ? '1 Red Pak tablet' : `${tablets} Red Pak tablets`;

    let recHtml = '';

    if (fastTier || slowTier) {
      const option = (tier, type, label, ctaLabel) => {
        if (!tier) return '';
        const price = tier.price ? `<span class="gas-calc__price">${tier.price}</span>` : '';
        const cta = tier.url ? `<a class="gas-calc__cta button" href="${tier.url}">${ctaLabel}</a>` : '';
        return `<article class="gas-calc__option gas-calc__option--${type}">
          <p class="gas-calc__rec-label">${label}</p>
          <p class="gas-calc__rec-name">${tier.name} ${price}</p>
          ${cta}
        </article>`;
      };
      recHtml = `<div class="gas-calc__options">
        ${option(fastTier, 'fast', 'Fast Release — rapid treatment', 'Shop Fast Release')}
        ${option(slowTier, 'slow', 'Slow Release — extended treatment', 'View Slow Release')}
      </div>
      <p class="gas-calc__rec-alt">Fast Release uses approximately ${tabletLabel}. Slow Release products are shown now and become purchasable September 15, 2026.</p>`;
    } else {
      const cta = this.fallbackUrl
        ? `<a class="gas-calc__cta button" href="${this.fallbackUrl}">See large-space options</a>`
        : '';
      recHtml = `
        <p class="gas-calc__rec-label">Large space</p>
        <p class="gas-calc__rec-name">This volume is above our standard kits.</p>
        <p class="gas-calc__rec-alt">Use ${tabletLabel}, combine multiple kits, or reach out for a tailored recommendation.</p>
        ${cta}
      `;
    }

    if (this.recEl) this.recEl.innerHTML = recHtml;
    if (this.result) this.result.hidden = false;
  }

  #showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
    if (this.result) this.result.hidden = true;
  }

  #clearError() {
    if (!this.errorEl) return;
    this.errorEl.textContent = '';
    this.errorEl.hidden = true;
  }
}

if (!customElements.get('gas-calculator')) {
  customElements.define('gas-calculator', GasCalculator);
}

